import { Injectable, Injector, StaticProvider } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef, ComponentType } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ModalOverlayRef } from '../class/modal-overlay-ref';
import { DIALOG_R_DATA } from '../injector/dialog-data.token';

export interface DialogConfig {
  panelClass?: string;
  hasBackdrop?: boolean;
  backdropClass?: string;
  data?: {}
}

const DEFAULT_CONFIG: DialogConfig = {
  hasBackdrop: true,
  backdropClass: 'rectangle-Copy',
  panelClass: 'tm-file-preview-dialog-panel'
}

import { InjectionToken } from '@angular/core';

export const DIALOG_DATA = new InjectionToken<any>('DIALOG_DATA');

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  constructor(private overlay: Overlay, private injector: Injector) { }

  open<T>(component: ComponentType<T>, config?: DialogConfig) {
    // Override default configuration
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };
    // Returns an OverlayRef which is a PortalHost
    const overlayRef = this.createOverlay(dialogConfig);

    // Instantiate remote control
    const dialogRef = new ModalOverlayRef(overlayRef);

    const overlayComponent = this.attachDialogContainer(overlayRef, dialogConfig, dialogRef, component);

    overlayRef.backdropClick().subscribe(_ => dialogRef.close());

    return dialogRef;
  }

  private createOverlay(config: DialogConfig) {
    const overlayConfig = this.getOverlayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  private getOverlayConfig(config: DialogConfig): OverlayConfig {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const overlayConfig = new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      positionStrategy
    });

    return overlayConfig;
  }

  private attachDialogContainer<T>(overlayRef: OverlayRef, config: DialogConfig, dialogRef: ModalOverlayRef, component: ComponentType<T>) {
    const injector = this.createInjector(config, dialogRef);

    const containerPortal = new ComponentPortal(component, null, injector);
    const containerRef = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }

  private createInjector(config: DialogConfig, dialogRef: ModalOverlayRef): Injector {
    const providers: StaticProvider[] = [
      { provide: ModalOverlayRef, useValue: dialogRef },
    ];
    if (config.data) {
      providers.push({ provide: DIALOG_R_DATA, useValue: config.data });
    }

    return Injector.create({ parent: this.injector, providers });
  }

}
